import { MouseEvent, useEffect, useMemo, useState } from "react";
import Plot from "react-plotly.js";
import { List, RowComponentProps } from "react-window";
import { useGetResultsetExplainabilityResultsetExplainabilityResultsetIdGet } from "../../../api/generated/fastAPI";
import { useMoleculeHighlighting } from "../../../hooks/endpoints/useMoleculeHighlighting";
import { ExplainabilityModelPayload, ExplainabilityMoleculeRow, ExplainabilityTopFeature } from "../../../api/models";
import SelectField from "../../../components/Form/SelectFields";
import BooleanField from "../../../components/Form/BooleanField";
import MoleculeStructure from "../../../components/Data/MoleculeStructure";
import HighlightComponent from "../../../components/Grid/GridComponentTypes/HighlightComponent";
import { downloadStructureImage } from "../../../utils/formattingFns";

import { ReactComponent as DownloadIcon } from "../../../assets/Icons/adornments/Download.svg";

interface ExplainabilityPayloadShape {
    resultset_id: number;
    structure_column?: string | null;
    models?: { [key: string]: ExplainabilityModelPayload };
}

interface AllModeHighlightCard {
    id: string;
    name: string;
    moleculeHighlightingData: Array<{
        dataframe: {
            atom_bond_importance: any;
            smiles: string;
        };
        metadata: any;
    }>;
    smiles: string;
    score: string | number | number[];
}

const SHAP_POSITIVE_COLOR = "#2563eb";
const SHAP_NEGATIVE_COLOR = "#dc2626";
const SHAP_POSITIVE_HIGHLIGHT_COLOR = "#60a5fa";
const SHAP_POSITIVE_NEUTRAL_COLOR = "#dbeafe";
const OOD_IN_DOMAIN_COLOR = "#2563eb";
const OOD_OUT_OF_DOMAIN_COLOR = "#dc2626";
const OOD_NOT_AVAILABLE_BAR_COLOR = "#6b7280";
const EMPTY_TOP_FEATURES: ExplainabilityTopFeature[] = [];
const DEFAULT_TOP_K = 1024;
const OOD_ESCALATED_TOP_K = 8192;
const OOD_SPARSITY_REFETCH_THRESHOLD = 0.2;
const OOD_SCALING_FLOOR = 0.15;

type AggregateDisplaySelection = "positive" | "negative" | "ood-scaling";

const formatExplainabilityTargetLabel = (target: string) => {
    if (target === "class_0") {
        return "Class 0";
    }
    if (target === "class_1") {
        return "Class 1";
    }
    if (target === "margin") {
        return "Margin";
    }
    if (target === "default") {
        return "Default";
    }
    return target;
};

const asExplainabilityPayload = (raw: any): ExplainabilityPayloadShape | undefined => {
    if (!raw) {
        return undefined;
    }

    const body = raw.data && typeof raw.data === "object" ? raw.data : raw;
    if (!body || typeof body !== "object") {
        return undefined;
    }

    return body as ExplainabilityPayloadShape;
};

const parseStructureAnalysisValue = (raw: unknown): any => {
    if (raw === null || raw === undefined) {
        return null;
    }
    if (typeof raw === "string") {
        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }
    if (typeof raw === "object") {
        return raw;
    }
    return null;
};

const sanitizeIndex = (raw: unknown): number | null => {
    const index = Number(raw);
    if (!Number.isFinite(index)) {
        return null;
    }
    const intIndex = Math.trunc(index);
    return intIndex >= 0 ? intIndex : null;
};

const extractIndices = (raw: unknown): number[] => {
    if (Array.isArray(raw)) {
        const indices = raw
            .map((item) => sanitizeIndex(item))
            .filter((item): item is number => item !== null);
        return Array.from(new Set(indices));
    }
    if (raw && typeof raw === "object") {
        const indices = Object.keys(raw)
            .map((key) => sanitizeIndex(key))
            .filter((item): item is number => item !== null);
        return Array.from(new Set(indices));
    }
    return [];
};

const getFeatureValue = (container: unknown, featureIndex: number): unknown => {
    if (!container || typeof container !== "object") {
        return undefined;
    }
    const typedContainer = container as any;
    return typedContainer[featureIndex] ?? typedContainer[String(featureIndex)];
};

const getFeatureMapping = (parsedStructureAnalysis: any, featureIndex: number): { atoms: number[], bonds: number[] } | null => {
    if (!parsedStructureAnalysis || featureIndex < 0) {
        return null;
    }

    const atomsByFeature = parsedStructureAnalysis?.atoms;
    const bondsByFeature = parsedStructureAnalysis?.bonds;
    if (atomsByFeature && bondsByFeature && typeof atomsByFeature === "object" && typeof bondsByFeature === "object") {
        const atoms = extractIndices(getFeatureValue(atomsByFeature, featureIndex));
        const bonds = extractIndices(getFeatureValue(bondsByFeature, featureIndex));
        if (atoms.length > 0 || bonds.length > 0) {
            return { atoms, bonds };
        }
    }

    const candidateContainers = [
        parsedStructureAnalysis?.features,
        parsedStructureAnalysis?.feature_map,
        parsedStructureAnalysis?.bits,
        parsedStructureAnalysis,
    ];

    for (const container of candidateContainers) {
        const candidate = getFeatureValue(container, featureIndex);
        if (!candidate || typeof candidate !== "object") {
            continue;
        }
        const candidateAtoms = extractIndices((candidate as any).atoms);
        const candidateBonds = extractIndices((candidate as any).bonds);
        if (candidateAtoms.length > 0 || candidateBonds.length > 0) {
            return { atoms: candidateAtoms, bonds: candidateBonds };
        }
    }

    if (Array.isArray(parsedStructureAnalysis)) {
        const candidate = parsedStructureAnalysis[featureIndex];
        if (candidate && typeof candidate === "object") {
            const candidateAtoms = extractIndices((candidate as any).atoms);
            const candidateBonds = extractIndices((candidate as any).bonds);
            if (candidateAtoms.length > 0 || candidateBonds.length > 0) {
                return { atoms: candidateAtoms, bonds: candidateBonds };
            }
        }
    }

    return null;
};

const computeColorDomain = (atomBondImportance?: { atoms: { [key: number]: number }, bonds: { [key: number]: number } }) => {
    if (!atomBondImportance) {
        return {
            min: 0,
            max: 0,
            absMax: 0,
            text: "[0, 0]",
        };
    }

    const values = Object.values(atomBondImportance.atoms)
        .concat(Object.values(atomBondImportance.bonds))
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value));

    if (values.length === 0) {
        return {
            min: 0,
            max: 0,
            absMax: 0,
            text: "[0, 0]",
        };
    }

    const min = Math.min(...values);
    const max = Math.max(...values);
    const absMax = Math.max(Math.abs(min), Math.abs(max));

    const text = min !== 0
        ? `[${min.toFixed(3)}, 0, ${max.toFixed(3)}]`
        : `[${min.toFixed(3)}, ${max.toFixed(3)}]`;

    return {
        min,
        max,
        absMax,
        text,
    };
};

const formatColorDomain = (min: number, max: number) => {
    if (min !== 0) {
        return `[${min.toFixed(3)}, 0, ${max.toFixed(3)}]`;
    }
    return `[${min.toFixed(3)}, ${max.toFixed(3)}]`;
};

const computeActiveColorDomainText = (
    domain: { min: number, max: number, absMax: number, text: string },
    normalize: boolean,
) => {
    if (!normalize || domain.absMax <= 0) {
        return domain.text;
    }

    return formatColorDomain(domain.min / domain.absMax, domain.max / domain.absMax);
};

const normalizeAtomBondImportance = (
    atomBondImportance?: { atoms: { [key: number]: number }, bonds: { [key: number]: number } },
    absMax?: number,
) => {
    if (!atomBondImportance || !absMax || absMax <= 0) {
        return atomBondImportance;
    }

    return {
        atoms: Object.fromEntries(
            Object.entries(atomBondImportance.atoms).map(([key, value]) => [Number(key), Number(value) / absMax])
        ) as { [key: number]: number },
        bonds: Object.fromEntries(
            Object.entries(atomBondImportance.bonds).map(([key, value]) => [Number(key), Number(value) / absMax])
        ) as { [key: number]: number },
    };
};

const getOodInfluenceLabel = (oodShapValue: number | null) => {
    if (oodShapValue === null || !Number.isFinite(oodShapValue)) {
        return "OOD Influence - Not available";
    }
    if (oodShapValue > 0) {
        return "OOD Influence - Out-of-Domain";
    }
    if (oodShapValue < 0) {
        return "OOD Influence - In-Domain";
    }
    return "OOD Influence - Neutral";
};

interface FeatureListRowProps {
    featureRows: ExplainabilityTopFeature[];
    selectedFeatureIndex: number;
    onSelectFeature: (featureIndex: number) => void;
    maxAbsShap: number;
    maxAbsOodFeatureShap: number;
    displayOodFeatureBars: boolean;
    oodFeatureShapByIndex: { [featureIndex: number]: number };
    formatFeatureValue: (feature: ExplainabilityTopFeature) => string;
}

function FeatureListRow({
    index,
    style,
    featureRows,
    selectedFeatureIndex,
    onSelectFeature,
    maxAbsShap,
    maxAbsOodFeatureShap,
    displayOodFeatureBars,
    oodFeatureShapByIndex,
    formatFeatureValue,
}: RowComponentProps<FeatureListRowProps>) {
    const feature = featureRows[index];
    if (!feature) {
        return <div style={style} />;
    }

    const shapValue = Number(feature.shap_value ?? 0);
    const widthPercent = maxAbsShap > 0 ? Math.max((Math.abs(shapValue) / maxAbsShap) * 100, 2) : 0;
    const hasOodValue = Object.prototype.hasOwnProperty.call(oodFeatureShapByIndex, feature.feature_index);
    const oodShapValue = hasOodValue ? Number(oodFeatureShapByIndex[feature.feature_index]) : null;
    const oodAbsWidthPercent = (oodShapValue !== null && maxAbsOodFeatureShap > 0)
        ? Math.max((Math.abs(oodShapValue) / maxAbsOodFeatureShap) * 50, 1)
        : 0;
    const oodIsOutOfDomainDirection = (oodShapValue ?? 0) > 0;
    const oodInfluenceLabel = getOodInfluenceLabel(oodShapValue);
    const oodDisplayText = oodShapValue === null ? "Not available" : oodShapValue.toFixed(4);
    const isSelectedFeature = selectedFeatureIndex === feature.feature_index;

    return (
        <div style={style} className="px-0.5 py-0">
            <button
                onClick={() => onSelectFeature(feature.feature_index)}
                className={`w-full flex flex-col gap-1 p-1 rounded transition-all ${isSelectedFeature ? "bg-secondary" : "hover:bg-secondary"}`}
            >
                <div className="w-full flex justify-between items-center gap-3 text-[12px] text-default">
                    <p>f{feature.feature_index} | value: {formatFeatureValue(feature)}</p>
                    <p style={{ color: shapValue >= 0 ? SHAP_POSITIVE_COLOR : SHAP_NEGATIVE_COLOR }}>{shapValue.toFixed(4)}</p>
                </div>
                <div className="w-full h-2 bg-light-outline rounded overflow-hidden">
                    <div
                        className="h-full"
                        style={{ width: `${widthPercent}%`, backgroundColor: shapValue >= 0 ? SHAP_POSITIVE_COLOR : SHAP_NEGATIVE_COLOR }}
                    />
                </div>
                {displayOodFeatureBars && (
                    <>
                        <div className="w-full flex justify-between items-center gap-3 text-[11px] text-quaternary">
                            <p>{oodInfluenceLabel}</p>
                            <p>{oodDisplayText}</p>
                        </div>
                        <div className="w-full flex justify-between items-center text-[10px] text-quaternary px-0.5">
                            <span>In-Domain</span>
                            <span>Out-of-Domain</span>
                        </div>
                        <div className="w-full h-2 bg-light-outline rounded relative overflow-hidden">
                            {oodShapValue === null && (
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: `repeating-linear-gradient(45deg, ${OOD_NOT_AVAILABLE_BAR_COLOR}, ${OOD_NOT_AVAILABLE_BAR_COLOR} 5px, rgba(107, 114, 128, 0.8) 5px, rgba(107, 114, 128, 0.8) 10px)`,
                                    }}
                                />
                            )}
                            <div className="absolute left-1/2 top-0 h-full w-[1px] bg-outline" />
                            {oodShapValue !== null && (
                                <div
                                    className="absolute top-0 h-full"
                                    style={oodIsOutOfDomainDirection
                                        ? {
                                            left: "50%",
                                            width: `${oodAbsWidthPercent}%`,
                                            background: `repeating-linear-gradient(45deg, ${OOD_OUT_OF_DOMAIN_COLOR}, ${OOD_OUT_OF_DOMAIN_COLOR} 4px, rgba(220, 38, 38, 0.75) 4px, rgba(220, 38, 38, 0.75) 8px)`,
                                        }
                                        : {
                                            right: "50%",
                                            width: `${oodAbsWidthPercent}%`,
                                            background: `repeating-linear-gradient(-45deg, ${OOD_IN_DOMAIN_COLOR}, ${OOD_IN_DOMAIN_COLOR} 4px, rgba(37, 99, 235, 0.75) 4px, rgba(37, 99, 235, 0.75) 8px)`,
                                        }}
                                />
                            )}
                        </div>
                    </>
                )}
            </button>
        </div>
    );
}

export default function ResultsetExplainabilityTab({
    resultsetId,
    resultsetDataframeV2,
    chosenModelFamilyId,
    modelHashLabels,
    compactSingleMoleculeMode = false,
}: {
    resultsetId: number,
    resultsetDataframeV2: any | null,
    chosenModelFamilyId: number | null,
    modelHashLabels: { [key: string]: string },
    compactSingleMoleculeMode?: boolean,
}) {
    const [ selectedMoleculeIndex, setSelectedMoleculeIndex ] = useState<number>(0);
    const [ selectedModelIndex, setSelectedModelIndex ] = useState<number>(0);
    const [ selectedTarget, setSelectedTarget ] = useState<string | null>(null);
    const [ panelMode, setPanelMode ] = useState<"all" | "shap">("shap");
    const [ topK, setTopK ] = useState<number>(DEFAULT_TOP_K);
    const [ detailHighlightMode, setDetailHighlightMode ] = useState<'structure' | 'shap-local' | 'shap-aggregate'>("structure");
    const [ normalizeColorDomain, setNormalizeColorDomain ] = useState<boolean>(false);
    const [ selectedFeatureIndex, setSelectedFeatureIndex ] = useState<number>(-1);
    const [ showOodFeatureBars, setShowOodFeatureBars ] = useState<boolean>(false);
    const [ aggregateDisplaySelections, setAggregateDisplaySelections ] = useState<AggregateDisplaySelection[]>(["positive", "negative"]);
    const [ oodEscalatedContextCache, setOodEscalatedContextCache ] = useState<{ [contextKey: string]: true }>({});

    const smilesList = useMemo(() => {
        if (!resultsetDataframeV2) {
            return [] as string[];
        }
        const smiles = resultsetDataframeV2["structure/SMILES"];
        return Array.isArray(smiles) ? (smiles as string[]) : [];
    }, [resultsetDataframeV2]);

    useEffect(() => {
        if (smilesList.length === 0) {
            setSelectedMoleculeIndex(0);
            return;
        }
        if (selectedMoleculeIndex > smilesList.length - 1) {
            setSelectedMoleculeIndex(0);
        }
    }, [smilesList, selectedMoleculeIndex]);

    const hasSingleMolecule = smilesList.length <= 1;
    const hideMoleculePanel = compactSingleMoleculeMode && hasSingleMolecule;
    const showGlobalShapPlot = !hideMoleculePanel;
    const oodTopK = Math.max(topK, DEFAULT_TOP_K);
    const oodCoverageContextKey = `${resultsetId}:${selectedModelIndex}:${selectedMoleculeIndex}:${selectedTarget ?? "default"}`;
    const hasEscalatedOodForContext = !!oodEscalatedContextCache[oodCoverageContextKey];
    const requestedOodTopK = hasEscalatedOodForContext ? OOD_ESCALATED_TOP_K : oodTopK;

    const explainabilityQuery = useGetResultsetExplainabilityResultsetExplainabilityResultsetIdGet(
        resultsetId,
        {
            top_k: topK,
            target: selectedTarget,
            include_structure_analysis: false,
            include_molecules: false,
        },
        {
            query: {
                enabled: !!resultsetId,
            },
        }
    );

    const explainabilityPayload = useMemo(() => asExplainabilityPayload(explainabilityQuery.data), [explainabilityQuery.data]);

    const modelFamilyHashes = useMemo(() => {
        if (!resultsetDataframeV2 || !chosenModelFamilyId) {
            return [] as string[];
        }
        const mapping = resultsetDataframeV2.model_family_mapping;
        if (!mapping) {
            return [] as string[];
        }
        const modelFamily = mapping[chosenModelFamilyId] ?? mapping[String(chosenModelFamilyId)];
        const modelHashes = modelFamily?.model_hashes;
        return Array.isArray(modelHashes) ? (modelHashes as string[]) : [];
    }, [resultsetDataframeV2, chosenModelFamilyId]);

    const modelEntries = useMemo<[string, ExplainabilityModelPayload][]>(() => {
        const allModels = explainabilityPayload?.models ?? {};
        const entries = Object.entries(allModels);

        if (modelFamilyHashes.length === 0) {
            return entries;
        }

        const familyHashSet = new Set(modelFamilyHashes);
        return entries.filter(([modelHash]) => familyHashSet.has(modelHash));
    }, [explainabilityPayload, modelFamilyHashes]);

    useEffect(() => {
        if (modelEntries.length === 0) {
            setSelectedModelIndex(0);
            return;
        }
        if (selectedModelIndex > modelEntries.length - 1) {
            setSelectedModelIndex(0);
        }
    }, [modelEntries, selectedModelIndex]);

    const selectedModelEntry = modelEntries[selectedModelIndex];
    const selectedModelHash = selectedModelEntry ? selectedModelEntry[0] : "";

    const beeswarmExplainabilityQuery = useGetResultsetExplainabilityResultsetExplainabilityResultsetIdGet(
        resultsetId,
        {
            model_hash: selectedModelHash || undefined,
            top_k: topK,
            target: selectedTarget,
            include_molecules: true,
            include_structure_analysis: false,
        },
        {
            query: {
                enabled: !!resultsetId && !!selectedModelHash && panelMode === "shap",
            },
        }
    );

    const detailExplainabilityQuery = useGetResultsetExplainabilityResultsetExplainabilityResultsetIdGet(
        resultsetId,
        {
            model_hash: selectedModelHash || undefined,
            molecule_index: selectedMoleculeIndex,
            top_k: topK,
            target: selectedTarget,
            include_structure_analysis: true,
        },
        {
            query: {
                enabled: !!resultsetId && !!selectedModelHash && panelMode === "shap",
            },
        }
    );

    const oodDetailExplainabilityQuery = useGetResultsetExplainabilityResultsetExplainabilityResultsetIdGet(
        resultsetId,
        {
            model_hash: selectedModelHash || undefined,
            molecule_index: selectedMoleculeIndex,
            top_k: requestedOodTopK,
            target: "y_prob_out_of_domain",
            include_structure_analysis: true,
        },
        {
            query: {
                enabled: !!resultsetId && !!selectedModelHash && panelMode === "shap" && selectedTarget !== "y_prob_out_of_domain",
            },
        }
    );

    const beeswarmPayload = useMemo(() => asExplainabilityPayload(beeswarmExplainabilityQuery.data), [beeswarmExplainabilityQuery.data]);
    const detailPayload = useMemo(() => asExplainabilityPayload(detailExplainabilityQuery.data), [detailExplainabilityQuery.data]);
    const oodDetailPayload = useMemo(() => asExplainabilityPayload(oodDetailExplainabilityQuery.data), [oodDetailExplainabilityQuery.data]);

    const selectedModelPayload = useMemo(() => {
        const base = selectedModelEntry ? selectedModelEntry[1] : undefined;
        if (!base) return undefined;
        const beeswarmModel = selectedModelHash ? beeswarmPayload?.models?.[selectedModelHash] : undefined;
        if (beeswarmModel?.molecules?.length) {
            return { ...base, molecules: beeswarmModel.molecules };
        }
        return base;
    }, [selectedModelEntry, selectedModelHash, beeswarmPayload]);
    const selectedModelDetailPayload = selectedModelHash ? detailPayload?.models?.[selectedModelHash] : undefined;
    const selectedModelOodDetailPayload = selectedModelHash ? oodDetailPayload?.models?.[selectedModelHash] : undefined;

    // On-demand molecule highlighting fetch (replaces preloaded MH from dataframe_v2)
    const moleculeHighlightingQuery = useMoleculeHighlighting(
        {
            resultsetId,
            modelHash: selectedModelHash || null,
            moleculeIndex: selectedMoleculeIndex,
        },
        !!resultsetId && !!selectedModelHash && panelMode === "all",
    );

    const selectedModelTargets = useMemo(() => {
        const availableTargets = selectedModelPayload?.available_targets;
        if (!Array.isArray(availableTargets)) {
            return [];
        }

        const normalizedTargets = availableTargets.filter((target) => typeof target === "string") as string[];
        const hasExplicitTargets = normalizedTargets.includes("class_0") || normalizedTargets.includes("class_1") || normalizedTargets.includes("margin");
        const filteredTargets = hasExplicitTargets
            ? normalizedTargets.filter((target) => target !== "default")
            : normalizedTargets;

        const uniqueTargets = Array.from(new Set(filteredTargets));
        const orderedTargets: string[] = [];
        if (uniqueTargets.includes("margin")) {
            orderedTargets.push("margin");
        }
        if (uniqueTargets.includes("class_1")) {
            orderedTargets.push("class_1");
        }
        if (uniqueTargets.includes("class_0")) {
            orderedTargets.push("class_0");
        }
        uniqueTargets.forEach((target) => {
            if (!orderedTargets.includes(target)) {
                orderedTargets.push(target);
            }
        });

        return orderedTargets;
    }, [selectedModelPayload]);

    const selectedModelPreferredTarget = selectedModelPayload?.selected_target;
    const isDcmExplainabilityContext = useMemo(() => {
        return selectedModelTargets.includes("y_prob_out_of_domain");
    }, [selectedModelTargets]);

    useEffect(() => {
        if (selectedModelTargets.length === 0) {
            // Avoid target oscillation while query data is temporarily unavailable during refetch.
            if (!explainabilityQuery.isLoading && !explainabilityQuery.isFetching && selectedTarget !== null) {
                setSelectedTarget(null);
            }
            return;
        }

        if (selectedTarget && selectedModelTargets.includes(selectedTarget)) {
            return;
        }

        const preferredTargets = [
            selectedModelPreferredTarget,
            "margin",
            "class_1",
            "class_0",
            selectedModelTargets[0],
        ].filter((target): target is string => typeof target === "string" && target.length > 0);

        const resolvedTarget = preferredTargets.find((target) => selectedModelTargets.includes(target));
        const nextTarget = resolvedTarget ?? selectedModelTargets[0];
        if (nextTarget !== selectedTarget) {
            setSelectedTarget(nextTarget);
        }
    }, [selectedModelPreferredTarget, selectedModelTargets, selectedTarget, explainabilityQuery.isLoading, explainabilityQuery.isFetching]);

    const selectedMolecule = useMemo(() => {
        if (!selectedModelPayload?.molecules || selectedModelPayload.molecules.length === 0) {
            return undefined;
        }
        return selectedModelPayload.molecules.find((molecule: ExplainabilityMoleculeRow) => molecule.row_index === selectedMoleculeIndex)
            ?? selectedModelPayload.molecules[0];
    }, [selectedModelPayload, selectedMoleculeIndex]);

    const selectedMoleculeOod = useMemo(() => {
        if (!selectedModelOodDetailPayload?.molecules || selectedModelOodDetailPayload.molecules.length === 0) {
            return undefined;
        }
        return selectedModelOodDetailPayload.molecules.find((molecule: ExplainabilityMoleculeRow) => molecule.row_index === selectedMoleculeIndex)
            ?? selectedModelOodDetailPayload.molecules[0];
    }, [selectedModelOodDetailPayload, selectedMoleculeIndex]);

    const selectedMoleculeSmiles = selectedMolecule?.structure ?? smilesList[selectedMoleculeIndex] ?? "";

    const featureRows = useMemo<ExplainabilityTopFeature[]>(() => {
        if (selectedMolecule?.top_features) {
            return selectedMolecule.top_features;
        }
        return EMPTY_TOP_FEATURES;
    }, [selectedMolecule]);

    const oodFeatureShapByIndex = useMemo(() => {
        const map: { [featureIndex: number]: number } = {};
        const oodFeatures = selectedMoleculeOod?.top_features ?? [];
        oodFeatures.forEach((feature: ExplainabilityTopFeature) => {
            const shapValue = Number(feature.shap_value ?? 0);
            if (!Number.isFinite(shapValue)) {
                return;
            }
            map[feature.feature_index] = shapValue;
        });
        return map;
    }, [selectedMoleculeOod]);

    const oodCoverageStats = useMemo(() => {
        const displayedFeatureIndices = featureRows.map((feature: ExplainabilityTopFeature) => feature.feature_index);
        if (displayedFeatureIndices.length === 0) {
            return {
                displayedCount: 0,
                overlapCount: 0,
                missingCount: 0,
                missingRatio: 0,
            };
        }

        let overlapCount = 0;
        displayedFeatureIndices.forEach((featureIndex: number) => {
            if (Object.prototype.hasOwnProperty.call(oodFeatureShapByIndex, featureIndex)) {
                overlapCount += 1;
            }
        });

        const displayedCount = displayedFeatureIndices.length;
        const missingCount = displayedCount - overlapCount;
        const missingRatio = displayedCount > 0 ? missingCount / displayedCount : 0;

        return {
            displayedCount,
            overlapCount,
            missingCount,
            missingRatio,
        };
    }, [featureRows, oodFeatureShapByIndex]);

    useEffect(() => {
        if (!isDcmExplainabilityContext || panelMode !== "shap" || selectedTarget === "y_prob_out_of_domain") {
            return;
        }
        if (!selectedModelHash || !resultsetId) {
            return;
        }
        if (hasEscalatedOodForContext) {
            return;
        }
        if (oodDetailExplainabilityQuery.isLoading || oodDetailExplainabilityQuery.isFetching) {
            return;
        }
        if (oodCoverageStats.displayedCount === 0) {
            return;
        }
        if (oodCoverageStats.missingRatio <= OOD_SPARSITY_REFETCH_THRESHOLD) {
            return;
        }

        setOodEscalatedContextCache((previousCache: { [contextKey: string]: true }) => {
            if (previousCache[oodCoverageContextKey]) {
                return previousCache;
            }
            return {
                ...previousCache,
                [oodCoverageContextKey]: true,
            };
        });
    }, [
        isDcmExplainabilityContext,
        panelMode,
        selectedTarget,
        selectedModelHash,
        resultsetId,
        hasEscalatedOodForContext,
        oodDetailExplainabilityQuery.isLoading,
        oodDetailExplainabilityQuery.isFetching,
        oodCoverageStats,
        oodCoverageContextKey,
    ]);

    const maxPositiveOodFeatureShap = useMemo(() => {
        const values = (Object.values(oodFeatureShapByIndex) as number[]).filter((value: number) => Number.isFinite(value) && value > 0);
        return values.length > 0 ? Math.max(...values) : 0;
    }, [oodFeatureShapByIndex]);

    const aggregatePositiveSelected = aggregateDisplaySelections.includes("positive");
    const aggregateNegativeSelected = aggregateDisplaySelections.includes("negative");
    const aggregateOodScalingSelected = aggregateDisplaySelections.includes("ood-scaling");
    const isOodTargetSelected = selectedTarget === "y_prob_out_of_domain";
    const hasOodFeatureSignal = oodCoverageStats.overlapCount > 0;
    const oodScalingAvailable = isDcmExplainabilityContext && hasOodFeatureSignal;
    const canUseOodOverlay = oodScalingAvailable && !isOodTargetSelected;
    const useOodScaling = aggregateOodScalingSelected && canUseOodOverlay;
    const canShowOodFeatureBars = isDcmExplainabilityContext && hasOodFeatureSignal && !isOodTargetSelected;
    const displayOodFeatureBars = showOodFeatureBars && canShowOodFeatureBars;
    const selectedFeatureHasOodValue = selectedFeatureIndex >= 0
        ? Object.prototype.hasOwnProperty.call(oodFeatureShapByIndex, selectedFeatureIndex)
        : false;
    const selectedFeatureMissingOodValue = selectedFeatureIndex >= 0 && !selectedFeatureHasOodValue;
    const isOodCoverageRefetching = hasEscalatedOodForContext && (oodDetailExplainabilityQuery.isLoading || oodDetailExplainabilityQuery.isFetching);
    const isOodCoverageStillLimited = hasEscalatedOodForContext
        && !oodDetailExplainabilityQuery.isLoading
        && !oodDetailExplainabilityQuery.isFetching
        && oodCoverageStats.displayedCount > 0
        && oodCoverageStats.missingRatio > OOD_SPARSITY_REFETCH_THRESHOLD;

    const getOodAttenuationMultiplier = (featureIndex: number) => {
        if (!useOodScaling || maxPositiveOodFeatureShap <= 0) {
            return 1;
        }

        const oodShap = oodFeatureShapByIndex[featureIndex];
        if (!Number.isFinite(oodShap) || oodShap <= 0) {
            return 1;
        }

        const normalized = Math.min(1, Math.max(0, oodShap / maxPositiveOodFeatureShap));
        return Math.max(OOD_SCALING_FLOOR, 1 - normalized);
    };

    const toggleAggregateDisplaySelection = (selection: AggregateDisplaySelection) => {
        setAggregateDisplaySelections((previousSelections: AggregateDisplaySelection[]) => {
            const selectionSet = new Set(previousSelections);
            if (selectionSet.has(selection)) {
                selectionSet.delete(selection);
            } else {
                selectionSet.add(selection);
            }

            if (!selectionSet.has("positive") && !selectionSet.has("negative")) {
                selectionSet.add("positive");
            }

            return Array.from(selectionSet) as AggregateDisplaySelection[];
        });
    };
    const maxAbsShap = Math.max(...featureRows.map((feature: ExplainabilityTopFeature) => Math.abs(Number(feature.shap_value ?? 0))), 0);
    const maxAbsOodFeatureShap = useMemo(() => {
        const values = (Object.values(oodFeatureShapByIndex) as number[])
            .map((value: number) => Math.abs(value))
            .filter((value: number) => Number.isFinite(value));
        return values.length > 0 ? Math.max(...values) : 0;
    }, [oodFeatureShapByIndex]);

    useEffect(() => {
        if (featureRows.length === 0) {
            setSelectedFeatureIndex(-1);
            return;
        }
        const hasFeature = featureRows.some((feature: ExplainabilityTopFeature) => feature.feature_index === selectedFeatureIndex);
        if (!hasFeature) {
            setSelectedFeatureIndex(featureRows[0].feature_index);
        }
    }, [featureRows, selectedFeatureIndex]);

    const globalRanking = useMemo(() => (selectedModelPayload?.feature_ranking ?? []).slice(0, Math.max(topK, 1)), [selectedModelPayload, topK]);

    const globalFeatureIndices = useMemo(() => {
        if (globalRanking.length > 0) {
            return globalRanking.map((entry: ExplainabilityTopFeature) => entry.feature_index);
        }
        const featureSet = new Set<number>();
        (selectedModelPayload?.molecules ?? []).forEach((row: ExplainabilityMoleculeRow) => {
            (row.top_features ?? []).forEach((feature: ExplainabilityTopFeature) => {
                featureSet.add(feature.feature_index);
            });
        });
        return Array.from(featureSet).slice(0, Math.max(topK, 1));
    }, [globalRanking, selectedModelPayload, topK]);

    const beeswarmFeatureIndices = useMemo(() => globalFeatureIndices.slice(0, 20), [globalFeatureIndices]);

    const globalFeaturePositionMap = useMemo(() => {
        const map: { [featureIndex: number]: number } = {};
        beeswarmFeatureIndices.forEach((featureIndex: number, position: number) => {
            map[featureIndex] = position;
        });
        return map;
    }, [beeswarmFeatureIndices]);

    const globalBeeswarmPoints = useMemo(() => {
        const points: { x: number, y: number, featureIndex: number, text: string, color: string }[] = [];

        (selectedModelPayload?.molecules ?? []).forEach((row: ExplainabilityMoleculeRow) => {
            (row.top_features ?? []).forEach((feature: ExplainabilityTopFeature, featurePosition: number) => {
                const featureIndex = feature.feature_index;
                const lane = globalFeaturePositionMap[featureIndex];
                if (lane === undefined) {
                    return;
                }

                const shapValue = Number(feature.shap_value ?? 0);
                if (Number.isNaN(shapValue)) {
                    return;
                }

                const jitter = ((row.row_index % 7) - 3) * 0.06 + ((featurePosition % 3) - 1) * 0.02;
                points.push({
                    x: shapValue,
                    y: lane + jitter,
                    featureIndex,
                    text: `molecule ${row.row_index} | f${featureIndex} | shap=${shapValue.toFixed(4)}`,
                    color: shapValue >= 0 ? SHAP_POSITIVE_COLOR : SHAP_NEGATIVE_COLOR,
                });
            });
        });

        return points;
    }, [selectedModelPayload, globalFeaturePositionMap]);

    const globalBarTrace = useMemo(() => {
        const values = beeswarmFeatureIndices.map((featureIndex: number) => {
            const ranking = globalRanking.find((entry: ExplainabilityTopFeature) => entry.feature_index === featureIndex);
            return Number(ranking?.mean_abs_shap ?? 0);
        });

        return {
            type: "bar" as const,
            orientation: "h" as const,
            x: values,
            y: beeswarmFeatureIndices.map((featureIndex: number) => globalFeaturePositionMap[featureIndex]),
            customdata: beeswarmFeatureIndices,
            marker: {
                color: beeswarmFeatureIndices.map((featureIndex: number) => featureIndex === selectedFeatureIndex ? SHAP_POSITIVE_HIGHLIGHT_COLOR : SHAP_POSITIVE_NEUTRAL_COLOR),
                opacity: 0.8,
            },
            hovertemplate: "f%{customdata} | mean |SHAP|=%{x:.4f}<extra></extra>",
            name: "Global mean |SHAP|",
        };
    }, [beeswarmFeatureIndices, globalFeaturePositionMap, globalRanking, selectedFeatureIndex]);

    const globalBeeswarmTrace = useMemo(() => {
        return {
            type: "scatter" as const,
            mode: "markers" as const,
            x: globalBeeswarmPoints.map((point: { x: number, y: number, featureIndex: number, text: string, color: string }) => point.x),
            y: globalBeeswarmPoints.map((point: { x: number, y: number, featureIndex: number, text: string, color: string }) => point.y),
            customdata: globalBeeswarmPoints.map((point: { x: number, y: number, featureIndex: number, text: string, color: string }) => point.featureIndex),
            text: globalBeeswarmPoints.map((point: { x: number, y: number, featureIndex: number, text: string, color: string }) => point.text),
            marker: {
                color: globalBeeswarmPoints.map((point: { x: number, y: number, featureIndex: number, text: string, color: string }) => point.color),
                size: 7,
                opacity: 0.75,
                line: {
                    width: 0,
                },
            },
            hovertemplate: "%{text}<extra></extra>",
            name: "Local SHAP points",
        };
    }, [globalBeeswarmPoints]);

    const featureRowHeight = displayOodFeatureBars ? 96 : 50;
    const onSelectFeature = (featureIndex: number) => {
        setSelectedFeatureIndex(featureIndex);
    };

    const onModelChange = (e: { value: string | number, label: string | number } | { value: string | number, label: string | number }[], name: string) => {
        if (Array.isArray(e)) {
            return;
        }
        const value = String(e.value);
        const modelIndex = modelEntries.findIndex(([modelHash]: [string, ExplainabilityModelPayload | undefined]) => modelHash === value);
        if (modelIndex >= 0) {
            setSelectedModelIndex(modelIndex);
        }
    };

    const modelOptionLabel = (modelHash: string) => {
        const method = modelHashLabels[modelHash];
        const shortHash = modelHash.slice(0, 8);
        if (method) {
            return `${method} (${shortHash})`;
        }
        return `Model ${shortHash}`;
    };

    const formatFeatureValue = (feature: ExplainabilityTopFeature) => {
        const featureValue = feature.feature_value;
        if (featureValue === undefined || featureValue === null) {
            return "N/A";
        }
        if (typeof featureValue === "number") {
            return featureValue.toFixed(4);
        }
        return String(featureValue);
    };

    const selectedModelDisplayName = selectedModelHash ? modelOptionLabel(selectedModelHash) : "";

    const selectedTargetLabel = useMemo(() => {
        if (!selectedTarget) {
            return "Primary";
        }
        return formatExplainabilityTargetLabel(selectedTarget);
    }, [selectedTarget]);

    const selectedModelHashMapping = useMemo(() => {
        if (!resultsetDataframeV2 || !selectedModelHash) {
            return undefined;
        }
        return resultsetDataframeV2?.model_hash?.[selectedModelHash];
    }, [resultsetDataframeV2, selectedModelHash]);

    const selectedModelScore = useMemo(() => {
        const predictions = selectedModelHashMapping?.predictions;
        if (!predictions) {
            return "";
        }

        const scoreArray = Array.isArray(predictions?.y_pred_proba)
            ? predictions.y_pred_proba
            : Array.isArray(predictions?.y_conf_pred)
                ? predictions.y_conf_pred
                : [];

        if (!Array.isArray(scoreArray) || selectedMoleculeIndex < 0 || selectedMoleculeIndex >= scoreArray.length) {
            return "";
        }

        return scoreArray[selectedMoleculeIndex] ?? "";
    }, [selectedModelHashMapping, selectedMoleculeIndex]);

    const allModeHighlightCards = useMemo<AllModeHighlightCard[]>(() => {
        if (!selectedModelHash || !selectedMoleculeSmiles) {
            return [];
        }

        // Use on-demand MH data from the dedicated endpoint
        const mhData = moleculeHighlightingQuery.data;
        const moleculeHighlightingEntries = mhData?.[selectedModelHash]?.molecule_highlighting ?? [];

        return moleculeHighlightingEntries.flatMap((entry: any, index: number): AllModeHighlightCard[] => {
            // molecule_index was passed to endpoint, so atom_bond_importance is a single-element array
            const atomBondImportanceRows = Array.isArray(entry?.atom_bond_importance) ? entry.atom_bond_importance : [];
            const atomBondImportance = atomBondImportanceRows[0];
            const metadata = entry?.metadata;

            if (!atomBondImportance || !metadata) {
                return [];
            }

            const jobMetadata = metadata?.job_metadata ?? {};
            const highlightTarget = typeof jobMetadata?.explainability_target === "string"
                ? String(jobMetadata.explainability_target)
                : null;

            if (selectedTarget) {
                const isLegacyEntry = !highlightTarget;
                const isMatchingTarget = highlightTarget === selectedTarget;
                if (!isLegacyEntry && !isMatchingTarget) {
                    return [];
                }
            }

            const featureImportanceMethod = jobMetadata?.feature_importance_method ? String(jobMetadata.feature_importance_method) : `Method ${index + 1}`;
            const featureImportanceType = jobMetadata?.feature_importance_type ? String(jobMetadata.feature_importance_type) : "";
            const targetLabel = highlightTarget
                ? (highlightTarget === "class_0" ? "Class 0" : highlightTarget === "class_1" ? "Class 1" : highlightTarget === "margin" ? "Margin" : highlightTarget)
                : null;
            const cardTitleBase = featureImportanceType ? `${featureImportanceMethod} (${featureImportanceType})` : featureImportanceMethod;
            const cardTitle = targetLabel ? `${cardTitleBase} | ${targetLabel}` : cardTitleBase;

            return [{
                id: `${selectedModelHash}-${featureImportanceMethod}-${featureImportanceType}-${highlightTarget ?? "legacy"}-${index}`,
                name: cardTitle,
                moleculeHighlightingData: [{
                    dataframe: {
                        atom_bond_importance: atomBondImportance,
                        smiles: selectedMoleculeSmiles,
                    },
                    metadata,
                }],
                smiles: selectedMoleculeSmiles,
                score: selectedModelScore,
            }];
        });
    }, [selectedModelHash, moleculeHighlightingQuery.data, selectedMoleculeSmiles, selectedModelScore, selectedTarget]);

    const noopSetPopupMessage = () => undefined;

    const selectedFeatureShapValue = useMemo(() => {
        if (selectedFeatureIndex < 0) {
            return 0;
        }
        const selectedFeature = featureRows.find((feature: ExplainabilityTopFeature) => feature.feature_index === selectedFeatureIndex);
        return Number(selectedFeature?.shap_value ?? 0);
    }, [featureRows, selectedFeatureIndex]);

    const selectedFeatureDisplayShapValue = useMemo(() => {
        if (!Number.isFinite(selectedFeatureShapValue) || selectedFeatureShapValue === 0) {
            return 0;
        }

        const sign = selectedFeatureShapValue >= 0 ? 1 : -1;
        const magnitude = maxAbsShap > 0 ? Math.min(1, Math.abs(selectedFeatureShapValue) / maxAbsShap) : 0;
        const attenuation = getOodAttenuationMultiplier(selectedFeatureIndex);
        return sign * magnitude * attenuation;
    }, [selectedFeatureShapValue, selectedFeatureIndex, maxAbsShap, maxPositiveOodFeatureShap, oodFeatureShapByIndex, useOodScaling]);

    const structureAnalysisRaw = selectedModelDetailPayload?.selected_molecule_structure_analysis?.value;
    const parsedStructureAnalysis = useMemo(() => parseStructureAnalysisValue(structureAnalysisRaw), [structureAnalysisRaw]);

    const selectedFeatureMapping = useMemo(() => {
        if (selectedFeatureIndex < 0) {
            return null;
        }
        return getFeatureMapping(parsedStructureAnalysis, selectedFeatureIndex);
    }, [parsedStructureAnalysis, selectedFeatureIndex]);

    const selectedFeatureAtomBondImportance = useMemo(() => {
        if (!selectedFeatureMapping) {
            return undefined;
        }

        if (detailHighlightMode === "structure") {
            return {
                atoms: Object.fromEntries(selectedFeatureMapping.atoms.map((atomIndex: number) => [atomIndex, 1])),
                bonds: Object.fromEntries(selectedFeatureMapping.bonds.map((bondIndex: number) => [bondIndex, 1])),
            };
        }

        const value = selectedFeatureDisplayShapValue;
        return {
            atoms: Object.fromEntries(selectedFeatureMapping.atoms.map((atomIndex: number) => [atomIndex, value])),
            bonds: Object.fromEntries(selectedFeatureMapping.bonds.map((bondIndex: number) => [bondIndex, value])),
        };
    }, [selectedFeatureMapping, detailHighlightMode, selectedFeatureDisplayShapValue]);

    const aggregatePositiveAtomBondImportance = useMemo(() => {
        if (detailHighlightMode !== "shap-aggregate") {
            return undefined;
        }
        if (!aggregatePositiveSelected) {
            return undefined;
        }

        const atomTotals: { [key: number]: number } = {};
        const bondTotals: { [key: number]: number } = {};

        featureRows.forEach((feature: ExplainabilityTopFeature) => {
            const shapValue = Number(feature.shap_value ?? 0);
            if (!Number.isFinite(shapValue) || shapValue <= 0) {
                return;
            }

            const attenuatedShapValue = shapValue * getOodAttenuationMultiplier(feature.feature_index);

            const mapping = getFeatureMapping(parsedStructureAnalysis, feature.feature_index);
            if (!mapping) {
                return;
            }

            mapping.atoms.forEach((atomIndex) => {
                atomTotals[atomIndex] = (atomTotals[atomIndex] ?? 0) + Math.abs(attenuatedShapValue);
            });
            mapping.bonds.forEach((bondIndex) => {
                bondTotals[bondIndex] = (bondTotals[bondIndex] ?? 0) + Math.abs(attenuatedShapValue);
            });
        });

        if (Object.keys(atomTotals).length === 0 && Object.keys(bondTotals).length === 0) {
            return undefined;
        }

        return {
            atoms: atomTotals,
            bonds: bondTotals,
        };
    }, [detailHighlightMode, featureRows, parsedStructureAnalysis, aggregatePositiveSelected, maxPositiveOodFeatureShap, oodFeatureShapByIndex, useOodScaling]);

    const aggregateNegativeAtomBondImportance = useMemo(() => {
        if (detailHighlightMode !== "shap-aggregate") {
            return undefined;
        }
        if (!aggregateNegativeSelected) {
            return undefined;
        }

        const atomTotals: { [key: number]: number } = {};
        const bondTotals: { [key: number]: number } = {};

        featureRows.forEach((feature: ExplainabilityTopFeature) => {
            const shapValue = Number(feature.shap_value ?? 0);
            if (!Number.isFinite(shapValue) || shapValue >= 0) {
                return;
            }

            const attenuatedShapValue = shapValue * getOodAttenuationMultiplier(feature.feature_index);

            const mapping = getFeatureMapping(parsedStructureAnalysis, feature.feature_index);
            if (!mapping) {
                return;
            }

            mapping.atoms.forEach((atomIndex) => {
                atomTotals[atomIndex] = (atomTotals[atomIndex] ?? 0) - Math.abs(attenuatedShapValue);
            });
            mapping.bonds.forEach((bondIndex) => {
                bondTotals[bondIndex] = (bondTotals[bondIndex] ?? 0) - Math.abs(attenuatedShapValue);
            });
        });

        if (Object.keys(atomTotals).length === 0 && Object.keys(bondTotals).length === 0) {
            return undefined;
        }

        return {
            atoms: atomTotals,
            bonds: bondTotals,
        };
    }, [detailHighlightMode, featureRows, parsedStructureAnalysis, aggregateNegativeSelected, maxPositiveOodFeatureShap, oodFeatureShapByIndex, useOodScaling]);

    const aggregateCombinedAtomBondImportance = useMemo(() => {
        if (detailHighlightMode !== "shap-aggregate") {
            return undefined;
        }

        const atomTotals: { [key: number]: number } = {};
        const bondTotals: { [key: number]: number } = {};

        Object.entries(aggregatePositiveAtomBondImportance?.atoms ?? {}).forEach(([atomIndex, value]) => {
            atomTotals[Number(atomIndex)] = (atomTotals[Number(atomIndex)] ?? 0) + Number(value);
        });
        Object.entries(aggregateNegativeAtomBondImportance?.atoms ?? {}).forEach(([atomIndex, value]) => {
            atomTotals[Number(atomIndex)] = (atomTotals[Number(atomIndex)] ?? 0) + Number(value);
        });

        Object.entries(aggregatePositiveAtomBondImportance?.bonds ?? {}).forEach(([bondIndex, value]) => {
            bondTotals[Number(bondIndex)] = (bondTotals[Number(bondIndex)] ?? 0) + Number(value);
        });
        Object.entries(aggregateNegativeAtomBondImportance?.bonds ?? {}).forEach(([bondIndex, value]) => {
            bondTotals[Number(bondIndex)] = (bondTotals[Number(bondIndex)] ?? 0) + Number(value);
        });

        if (Object.keys(atomTotals).length === 0 && Object.keys(bondTotals).length === 0) {
            return undefined;
        }

        return {
            atoms: atomTotals,
            bonds: bondTotals,
        };
    }, [detailHighlightMode, aggregatePositiveAtomBondImportance, aggregateNegativeAtomBondImportance]);

    const positiveAggregateColorDomain = useMemo(() => computeColorDomain(aggregatePositiveAtomBondImportance), [aggregatePositiveAtomBondImportance]);
    const negativeAggregateColorDomain = useMemo(() => computeColorDomain(aggregateNegativeAtomBondImportance), [aggregateNegativeAtomBondImportance]);
    const combinedAggregateColorDomain = useMemo(() => computeColorDomain(aggregateCombinedAtomBondImportance), [aggregateCombinedAtomBondImportance]);
    const aggregateSharedAbsMax = useMemo(() => {
        return Math.max(positiveAggregateColorDomain.absMax, negativeAggregateColorDomain.absMax, combinedAggregateColorDomain.absMax);
    }, [positiveAggregateColorDomain.absMax, negativeAggregateColorDomain.absMax, combinedAggregateColorDomain.absMax]);

    const combinedAggregateSharedDomain = useMemo(() => {
        if (aggregateSharedAbsMax <= 0) {
            return combinedAggregateColorDomain;
        }
        return {
            ...combinedAggregateColorDomain,
            absMax: aggregateSharedAbsMax,
        };
    }, [combinedAggregateColorDomain, aggregateSharedAbsMax]);

    const combinedAggregateActiveDomainText = useMemo(
        () => computeActiveColorDomainText(combinedAggregateSharedDomain, normalizeColorDomain),
        [combinedAggregateSharedDomain, normalizeColorDomain]
    );
    const selectedAggregateActiveDomainText = combinedAggregateActiveDomainText;

    const displayShapLocalAtomBondImportance = selectedFeatureAtomBondImportance;

    const displayCombinedAggregateAtomBondImportance = useMemo(
        () => normalizeColorDomain
            ? normalizeAtomBondImportance(aggregateCombinedAtomBondImportance, aggregateSharedAbsMax)
            : aggregateCombinedAtomBondImportance,
        [normalizeColorDomain, aggregateCombinedAtomBondImportance, aggregateSharedAbsMax]
    );

    const downloadShapStructure = (event: MouseEvent<HTMLButtonElement>, structureId: string, modeSuffix: string) => {
        const hashPart = selectedModelHash ? selectedModelHash.slice(0, 8) : "model";
        const identifier = `explainability-${hashPart}-mol-${selectedMoleculeIndex}-${modeSuffix}`;
        downloadStructureImage(event, identifier, structureId);
    };

    return (
        <div className="mt-6 h-full min-h-[60vh] flex flex-col gap-4">
            <div className="w-full grid grid-cols-1 md:grid-cols-[minmax(260px,460px)_auto] items-end gap-4">
                <div className="min-w-0 max-w-[460px] w-full">
                    <SelectField
                        label="Model"
                        id="explainability-model"
                        name="explainability-model"
                        value={selectedModelHash ? { value: selectedModelHash, label: modelOptionLabel(selectedModelHash) } : null}
                        options={modelEntries.map(([modelHash]: [string, ExplainabilityModelPayload | undefined]) => ({ value: modelHash, label: modelOptionLabel(modelHash) }))}
                        isRequired={false}
                        isCreatable={false}
                        isMulti={false}
                        handleChange={onModelChange as any}
                        disabled={modelEntries.length === 0 || explainabilityQuery.isLoading}
                    />
                </div>

                <div className="justify-self-start md:justify-self-end">
                    <div className="w-fit flex flex-wrap items-end gap-3 md:justify-end">
                        {selectedModelTargets.length > 1 && (
                            <div className="w-fit">
                                <p className="text-[12px] font-medium text-default mb-2">Target</p>
                                <div className="inline-flex h-[44px] border border-outline rounded-lg p-1 items-center gap-1">
                                    {selectedModelTargets.map((target: string) => {
                                        const isSelected = selectedTarget === target;
                                        const label = formatExplainabilityTargetLabel(target);

                                        return (
                                            <button
                                                key={target}
                                                onClick={() => setSelectedTarget(target)}
                                                className={`px-3 h-full rounded text-[13px] transition-all ${isSelected ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                            >
                                                {label}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        <div className="w-fit">
                            <p className="text-[12px] font-medium text-default mb-2">View</p>
                            <div className="inline-flex h-[44px] border border-outline rounded-lg p-1 items-center gap-1">
                                <button
                                    onClick={() => setPanelMode("all")}
                                    className={`px-3 h-full rounded text-[13px] transition-all ${panelMode === "all" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                >
                                    All
                                </button>
                                <button
                                    onClick={() => setPanelMode("shap")}
                                    className={`px-3 h-full rounded text-[13px] transition-all ${panelMode === "shap" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                >
                                    SHAP
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-full h-full grid grid-cols-1 lg:grid-cols-12 gap-4 min-h-[62vh]">
                {!hideMoleculePanel && (
                <div className="lg:col-span-4 border border-outline rounded-lg bg-white p-4 flex flex-col gap-3 min-h-0">
                    <p className="font-semibold text-default">Molecules</p>
                    <div className="flex-1 overflow-y-auto pr-2 flex flex-col gap-3 min-h-0">
                        {smilesList.length > 0 ? smilesList.map((smiles: string, index: number) => (
                            <button
                                key={`${index}-${smiles}`}
                                onClick={() => setSelectedMoleculeIndex(index)}
                                className={`w-full text-left border rounded-lg p-3 transition-all ${index === selectedMoleculeIndex ? "border-primary bg-secondary" : "border-outline bg-white hover:border-primary"}`}
                            >
                                <div className="flex justify-between items-center gap-2 mb-2">
                                    <p className="text-[12px] font-semibold text-default">Molecule #{index}</p>
                                </div>
                                <div className="w-full flex justify-center items-center">
                                    <MoleculeStructure smiles={smiles} width={140} height={120} />
                                </div>
                            </button>
                        )) : (
                            <p className="text-[14px] text-default">No molecules available for this resultset.</p>
                        )}
                    </div>
                </div>
                )}

                <div className={`${hideMoleculePanel ? "lg:col-span-12" : "lg:col-span-8"} flex flex-col gap-4 min-h-0`}>
                    {panelMode === "all" ? (
                        <div className="border border-outline rounded-lg bg-white p-4">
                            <p className="font-semibold text-default">Molecule Highlighting (All Methods)</p>
                            {!selectedModelHash ? (
                                <p className="text-[14px] text-default mt-3">Select a model to view molecule highlighting methods.</p>
                            ) : moleculeHighlightingQuery.isLoading ? (
                                <div className="flex items-center gap-2 mt-3">
                                    <div className="animate-spin h-4 w-4 border-2 border-primary border-t-transparent rounded-full" />
                                    <p className="text-[14px] text-default">Loading molecule highlighting...</p>
                                </div>
                            ) : allModeHighlightCards.length === 0 ? (
                                <p className="text-[14px] text-default mt-3">No molecule highlighting methods available for this model and molecule.</p>
                            ) : (
                                <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-4">
                                    {allModeHighlightCards.map((card: AllModeHighlightCard) => (
                                        <div key={card.id} className="border border-light-outline rounded-lg bg-white overflow-hidden">
                                            <div className="px-4 pt-3">
                                                <p className="text-[13px] font-semibold text-default">{card.name}</p>
                                            </div>
                                            <HighlightComponent
                                                data={card}
                                                setPopupMessage={noopSetPopupMessage}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        <>
                    <div className="border border-outline rounded-lg bg-white p-4">
                        <div className="w-full grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
                            <div className="flex flex-col gap-1">
                                <p className="font-semibold text-default">SHAP Feature Panel</p>
                                <p className="text-[14px] text-default">
                                    Model: {selectedModelDisplayName || "-"}
                                </p>
                                {selectedModelTargets.length > 0 && (
                                    <p className="text-[14px] text-default">
                                        Target: {selectedTargetLabel}
                                    </p>
                                )}
                            </div>
                            <div className="xl:justify-self-end flex flex-wrap items-end gap-4">
                                <div className="flex flex-col gap-1 min-w-[110px]">
                                    <p className="text-[12px] font-medium text-default">Top K Features</p>
                                    <input
                                        type="number"
                                        value={topK}
                                        onChange={(event) => {
                                            const value = Number(event.target.value);
                                            setTopK(Number.isNaN(value) ? DEFAULT_TOP_K : value);
                                        }}
                                        className="w-[90px] h-[44px] border border-outline rounded px-3 text-[13px] text-default"
                                    />
                                </div>
                                {isDcmExplainabilityContext && (
                                    <div className="flex flex-col gap-1 min-w-[220px] pb-0">
                                        <BooleanField
                                            label="Show OOD Overlay Bars"
                                            id="show-ood-overlay-bars"
                                            name="showOodOverlayBars"
                                            value={displayOodFeatureBars}
                                            isRequired={false}
                                            handleChange={(_, __) => setShowOodFeatureBars((previous: boolean) => !previous)}
                                            disabled={!canShowOodFeatureBars}
                                        />
                                        {isOodCoverageRefetching && (
                                            <p className="text-[12px] text-quaternary">Fetching expanded OOD coverage...</p>
                                        )}
                                        {isOodCoverageStillLimited && (
                                            <p className="text-[12px] text-quaternary">OOD coverage is still limited for some features.</p>
                                        )}
                                        {isOodTargetSelected && (
                                            <p className="text-[12px] text-quaternary">OOD overlay bars are unavailable while OOD is the selected target.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                        {explainabilityQuery.isLoading ? (
                            <p className="text-[14px] text-default mt-3">Loading explainability data...</p>
                        ) : explainabilityQuery.isError ? (
                            <p className="text-[14px] text-warning mt-3">Unable to load explainability data for this resultset.</p>
                        ) : !selectedModelPayload ? (
                            <p className="text-[14px] text-default mt-3">No explainability payload is available for the selected model family.</p>
                        ) : !selectedModelPayload.has_shap ? (
                            <div className="mt-3 flex flex-col gap-3">
                                <p className="text-[14px] text-default">SHAP is not available for this model. Showing fallback feature ranking.</p>
                                <div className="max-h-[240px] overflow-auto border border-light-outline rounded">
                                    <table className="w-full text-[13px] text-default">
                                        <thead>
                                            <tr className="border-b border-light-outline text-left">
                                                <th className="py-2 px-3">Rank</th>
                                                <th className="py-2 px-3">Feature</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(selectedModelPayload.feature_index_fallback ?? []).slice(0, Math.max(topK, 1)).map((featureIndex, idx) => (
                                                <tr key={`${featureIndex}-${idx}`} className="border-b border-light-outline">
                                                    <td className="py-2 px-3">{idx + 1}</td>
                                                    <td className="py-2 px-3">f{featureIndex}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        ) : (
                            <div className="mt-3 flex flex-col gap-3">
                                <div className={`grid grid-cols-1 ${showGlobalShapPlot ? "xl:grid-cols-2" : ""} gap-4`}>
                                    {showGlobalShapPlot && (
                                    <div className="border border-light-outline rounded p-3 h-[320px]">
                                        {beeswarmFeatureIndices.length > 0 ? (
                                            <Plot
                                                data={[globalBarTrace, globalBeeswarmTrace]}
                                                layout={{
                                                    margin: { t: 10, r: 10, b: 40, l: 80 },
                                                    barmode: "overlay",
                                                    showlegend: false,
                                                    xaxis: { title: "SHAP value / mean |SHAP|" },
                                                    yaxis: {
                                                        tickmode: "array",
                                                        tickvals: beeswarmFeatureIndices.map((featureIndex) => globalFeaturePositionMap[featureIndex]),
                                                        ticktext: beeswarmFeatureIndices.map((featureIndex) => `f${featureIndex}`),
                                                        autorange: "reversed",
                                                    },
                                                    paper_bgcolor: "#ffffff",
                                                    plot_bgcolor: "#ffffff",
                                                    height: 300,
                                                }}
                                                config={{ displayModeBar: false, responsive: true }}
                                                className="w-full"
                                                onClick={(event) => {
                                                    const clickedFeature = event?.points?.[0]?.customdata;
                                                    if (clickedFeature !== undefined && clickedFeature !== null) {
                                                        setSelectedFeatureIndex(Number(clickedFeature));
                                                    }
                                                }}
                                            />
                                        ) : (
                                            <p className="text-[13px] text-default">No global SHAP data available (beeswarm capped at top 20 features).</p>
                                        )}
                                    </div>
                                    )}

                                    <div className="h-[320px] border border-light-outline rounded p-3">
                                        {featureRows.length > 0 ? (
                                            <List
                                                rowComponent={FeatureListRow}
                                                rowCount={featureRows.length}
                                                rowHeight={featureRowHeight}
                                                rowProps={{
                                                    featureRows,
                                                    selectedFeatureIndex,
                                                    onSelectFeature,
                                                    maxAbsShap,
                                                    maxAbsOodFeatureShap,
                                                    displayOodFeatureBars,
                                                    oodFeatureShapByIndex,
                                                    formatFeatureValue,
                                                }}
                                            />
                                        ) : (
                                            <p className="text-[13px] text-default">No local SHAP features for selected molecule.</p>
                                        )}
                                    </div>
                                </div>
                                <p className="text-[12px] text-quaternary">
                                    Selected feature: {selectedFeatureIndex >= 0 ? `f${selectedFeatureIndex}` : "None"}
                                </p>
                            </div>
                        )}
                    </div>

                    <div className="border border-outline rounded-lg bg-white p-4 grow min-h-[260px]">
                        <div className="w-full grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto] items-start gap-4">
                            <div className="flex flex-col gap-2">
                                <p className="font-semibold text-default">Selected Molecule Details</p>
                                <div className="w-fit">
                                    <p className="text-[12px] font-medium text-default mb-2">Highlight Mode</p>
                                    <div className="inline-flex h-[44px] border border-outline rounded-lg p-1 items-center gap-1">
                                        <button
                                            onClick={() => setDetailHighlightMode("structure")}
                                            className={`px-3 h-full rounded text-[13px] transition-all ${detailHighlightMode === "structure" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                        >
                                            Structure
                                        </button>
                                        <button
                                            onClick={() => setDetailHighlightMode("shap-local")}
                                            className={`px-3 h-full rounded text-[13px] transition-all ${detailHighlightMode === "shap-local" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                        >
                                            SHAP-Local
                                        </button>
                                        <button
                                            onClick={() => setDetailHighlightMode("shap-aggregate")}
                                            className={`px-3 h-full rounded text-[13px] transition-all ${detailHighlightMode === "shap-aggregate" ? "bg-secondary text-primary" : "text-default hover:bg-secondary"}`}
                                        >
                                            Aggregate SHAP
                                        </button>
                                    </div>
                                </div>
                                {detailHighlightMode === "shap-local" && isDcmExplainabilityContext && (
                                    <label className="mt-1 flex items-center gap-2 text-[12px] text-default cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={useOodScaling}
                                            disabled={!canUseOodOverlay}
                                            onChange={() => toggleAggregateDisplaySelection("ood-scaling")}
                                        />
                                        Scale Highlights by OOD
                                    </label>
                                )}
                                {detailHighlightMode === "shap-local" && isDcmExplainabilityContext && isOodTargetSelected && (
                                    <p className="text-[12px] text-quaternary">OOD overlay is unavailable while OOD is the selected target.</p>
                                )}
                                {detailHighlightMode === "shap-local" && isDcmExplainabilityContext && !isOodTargetSelected && selectedFeatureMissingOodValue && (
                                    <p className="text-[12px] text-quaternary">Selected feature has no matching OOD value in current coverage.</p>
                                )}
                            </div>
                            <div className="xl:justify-self-end flex flex-col gap-1 min-w-[110px]">
                                <p className="text-[12px] font-medium text-default">Top K Features</p>
                                <input
                                    type="number"
                                    value={topK}
                                    onChange={(event) => {
                                        const value = Number(event.target.value);
                                        setTopK(Number.isNaN(value) ? DEFAULT_TOP_K : value);
                                    }}
                                    className="w-[90px] h-[44px] border border-outline rounded px-3 text-[13px] text-default"
                                />
                            </div>
                        </div>
                        {selectedMoleculeSmiles ? (
                            <div className="mt-3 grid grid-cols-1 gap-4">
                                <div className="w-full flex flex-col gap-2">
                                    {detailHighlightMode === "shap-aggregate" ? (
                                        <div className="w-full grid grid-cols-1 lg:grid-cols-[auto_minmax(220px,260px)] gap-3 items-start">
                                            <div className="border border-light-outline rounded p-2 w-fit">
                                                <div className="w-full flex justify-between items-center mb-1">
                                                    <p className="text-[12px] font-semibold text-default">Aggregate SHAP</p>
                                                    <button
                                                        onClick={(event) => downloadShapStructure(event, "explainability-selected-molecule-aggregate", "shap-aggregate")}
                                                        className="w-[24px] text-disable hover:text-primary transition-all"
                                                        title="Download Aggregate SHAP Image"
                                                    >
                                                        <DownloadIcon />
                                                    </button>
                                                </div>
                                                <MoleculeStructure
                                                    smiles={selectedMoleculeSmiles}
                                                    width={320}
                                                    height={240}
                                                    id="explainability-selected-molecule-aggregate"
                                                    atom_bond_importance={displayCombinedAggregateAtomBondImportance}
                                                    highlightColorMode="signed"
                                                />
                                                {!displayCombinedAggregateAtomBondImportance && (
                                                    <p className="text-[12px] text-default">No mapped contributions for current aggregate selection.</p>
                                                )}
                                            </div>

                                            <div className="border border-light-outline rounded p-2 w-full">
                                                <p className="text-[12px] font-medium text-default mb-2">Aggregate Display</p>
                                                <div className="flex flex-col gap-1">
                                                    {([
                                                        { key: "positive", label: "Positive Features" },
                                                        { key: "negative", label: "Negative Features" },
                                                        { key: "ood-scaling", label: "OOD Scaling" },
                                                    ] as { key: AggregateDisplaySelection, label: string }[]).map((option) => {
                                                        const isSelected = option.key === "ood-scaling"
                                                            ? useOodScaling
                                                            : aggregateDisplaySelections.includes(option.key);
                                                        const isDisabled = option.key === "ood-scaling" && !canUseOodOverlay;
                                                        return (
                                                            <button
                                                                key={option.key}
                                                                type="button"
                                                                disabled={isDisabled}
                                                                onClick={() => toggleAggregateDisplaySelection(option.key)}
                                                                className={`w-full h-[32px] rounded px-2 text-left text-[12px] transition-all ${isSelected ? "bg-secondary text-primary" : "text-default hover:bg-secondary"} ${isDisabled ? "opacity-40 cursor-not-allowed hover:bg-transparent" : ""}`}
                                                            >
                                                                {option.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                                {isOodTargetSelected && (
                                                    <p className="text-[12px] text-quaternary mt-2">OOD overlay is unavailable while OOD is the selected target.</p>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="w-fit">
                                            <div className="w-full flex justify-end items-center mb-1">
                                                <button
                                                    onClick={(event) => downloadShapStructure(event, "explainability-selected-molecule", "shap-local")}
                                                    className="w-[24px] text-disable hover:text-primary transition-all"
                                                    title="Download SHAP Molecule Image"
                                                >
                                                    <DownloadIcon />
                                                </button>
                                            </div>
                                            <MoleculeStructure
                                                smiles={selectedMoleculeSmiles}
                                                width={320}
                                                height={260}
                                                id="explainability-selected-molecule"
                                                atom_bond_importance={displayShapLocalAtomBondImportance}
                                                highlightColorMode={detailHighlightMode === "structure" ? "yellow" : "signed"}
                                            />
                                        </div>
                                    )}

                                    {detailHighlightMode === "shap-aggregate" && (
                                        <div className="w-full border border-light-outline rounded p-2">
                                            <div className="flex flex-col gap-1">
                                                <p className="text-[12px] text-default">Selected Domain: <span className="font-medium">{selectedAggregateActiveDomainText}</span></p>
                                                <p className="text-[12px] text-default">Selection: <span className="font-medium">{aggregatePositiveSelected && aggregateNegativeSelected ? "Positive + Negative" : aggregatePositiveSelected ? "Positive" : "Negative"}{useOodScaling ? " + OOD Scaling" : ""}</span></p>
                                            </div>
                                            <label className="mt-2 flex items-center gap-2 text-[12px] text-default cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    checked={normalizeColorDomain}
                                                    onChange={(event) => setNormalizeColorDomain(event.target.checked)}
                                                />
                                                Normalize
                                            </label>
                                        </div>
                                    )}
                                    <p className="text-[13px] text-default">Molecule Index: {selectedMoleculeIndex}</p>
                                    <p className="text-[13px] text-default">Base Value: {selectedMolecule?.base_value ?? "N/A"}</p>
                                    <p className="text-[13px] text-default">Prediction Value: {selectedMolecule?.prediction_value ?? "N/A"}</p>
                                    {detailHighlightMode !== "shap-aggregate" && !selectedFeatureMapping && (
                                        <p className="text-[13px] text-default">No atom/bond mapping found for selected feature.</p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <p className="text-[13px] text-default mt-3">Select a molecule from the left panel.</p>
                        )}
                    </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
